import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, History, Info, PauseCircle, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAppReleaseSettings, updateAppReleaseSettings } from "@/api/system";

const PLATFORM = "android";
const emptySettings = {
  default_update_type: "flexible",
  minimum_supported_build: 1,
  title: "Ny version av Lapstr",
  message: "En ny version finns tillgänglig med förbättringar och korrigeringar.",
  release_notes: "",
  enabled: false,
  history: [],
};

function normalizeSettings(value) {
  return {
    ...emptySettings,
    ...(value || {}),
    default_update_type: value?.default_update_type === "immediate" ? "immediate" : "flexible",
    minimum_supported_build: Math.max(1, Number(value?.minimum_supported_build || 1)),
  };
}

function derivePreview(build, settings) {
  if (!settings.enabled) {
    return { label: "Pausad", variant: "secondary", description: "Ingen uppdateringsdialog visas." };
  }
  if (build < settings.minimum_supported_build) {
    return { label: "Obligatorisk", variant: "destructive", description: "Google Plays Immediate-flöde används." };
  }
  if (settings.default_update_type === "immediate") {
    return { label: "Obligatorisk", variant: "destructive", description: "Alla tillgängliga uppdateringar använder Immediate." };
  }
  return { label: "Flexibel", variant: "default", description: "Användaren kan fortsätta medan Google Play laddar ned." };
}

function formatHistoryDate(value) {
  if (!value) return "Okänd tid";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Okänd tid";
  return new Intl.DateTimeFormat("sv-SE", { dateStyle: "short", timeStyle: "short" }).format(date);
}

const STEPS = [
  {
    title: "Pausa den nuvarande policyn",
    body: "Klicka på Nödpausa innan den nya AAB-filen publiceras. Versionsinformationen här är global och inte bunden till en viss build. Pausen förhindrar därför att en automatiskt upptäckt Play-uppdatering visas tillsammans med föregående versions rubrik och nyheter.",
  },
  {
    title: "Bygg och verifiera releasen",
    body: "Höj versionName och versionCode och bygg en signerad AAB. Kontrollera produktions-API, inloggning, push, djuplänkar och centrala appflöden innan filen laddas upp.",
  },
  {
    title: "Publicera och invänta Google Play",
    body: "Ladda upp AAB-filen till rätt test- eller produktionskanal. Vänta tills versionen är godkänd och faktiskt går att installera för kanalens användare. Play Core kan upptäcka versionen automatiskt så snart den blir tillgänglig.",
  },
  {
    title: "Förbered den nya uppdateringsinformationen",
    body: "Skriv rubrik, meddelande och Nyheter i versionen för den publicerade releasen. Välj Flexible för en rekommenderad uppdatering eller Immediate för Googles blockerande helskärmsflöde. Höj endast Lägsta stödda build om äldre installationer verkligen måste spärras.",
  },
  {
    title: "Aktivera uppdateringskontrollen",
    body: "Aktivera policyn och spara först när den nya versionen går att installera och all information ovan avser just den releasen. När appen därefter startar kombineras Play Cores tillgängliga version med texten och beteendet i denna policy.",
  },
  {
    title: "Verifiera med en äldre Play-installation",
    body: "Öppna en äldre appversion som installerats från samma Play-kanal och kontrollera dialog, versionsinformation och valt uppdateringsflöde. Lapstr Dev och vanliga sideloadade APK-filer kan inte verifiera Play Core-flödet korrekt.",
  },
];

export default function AdminAppReleasesPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(emptySettings);
  const [simulatedBuild, setSimulatedBuild] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    if (!isSuper) return;
    let active = true;
    setLoading(true);
    fetchAppReleaseSettings(PLATFORM)
      .then((data) => {
        if (!active) return;
        const normalized = normalizeSettings(data);
        setSettings(normalized);
        setSimulatedBuild(Math.max(1, normalized.minimum_supported_build - 1));
      })
      .catch((error) => toast.error(error?.message || "Kunde inte hämta uppdateringspolicyn."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [isSuper]);

  const preview = useMemo(
    () => derivePreview(Number(simulatedBuild || 1), settings),
    [settings, simulatedBuild]
  );

  const setField = (field, value) => setSettings((current) => ({ ...current, [field]: value }));

  const save = async (overrides = {}) => {
    if (saving) return;
    const payload = {
      default_update_type: settings.default_update_type,
      minimum_supported_build: Math.max(1, Number(settings.minimum_supported_build || 1)),
      title: settings.title.trim(),
      message: settings.message.trim(),
      release_notes: settings.release_notes.trim(),
      enabled: Boolean(settings.enabled),
      ...overrides,
    };
    setSaving(true);
    try {
      const updated = await updateAppReleaseSettings(PLATFORM, payload);
      setSettings(normalizeSettings(updated));
      toast.success(overrides.enabled === false ? "Uppdateringskontrollen är pausad." : "Uppdateringspolicyn är sparad.");
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara uppdateringspolicyn.");
    } finally {
      setSaving(false);
    }
  };

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AdminHeader />
        <main className="container mx-auto p-4 md:p-8">
          <Card><CardContent className="p-6">Den här sidan kräver superadministratör.</CardContent></Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <AdminHeader />
      <main className="container mx-auto max-w-6xl space-y-6 p-4 md:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Google Play</p>
            <h1 className="text-3xl font-bold tracking-tight">Appuppdateringar</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Google Play identifierar nya versioner och installerar dem. Här styr du hur Lapstr ska kommunicera och kräva uppdateringen.
            </p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm"><Info className="mr-2 h-4 w-4" /> Så fungerar det</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Arbetsflöde för appuppdateringar</DialogTitle>
                <DialogDescription>Google Play avgör om en uppdatering finns. Lapstr styr endast hur den kommuniceras och om den ska vara flexibel eller obligatorisk.</DialogDescription>
              </DialogHeader>
              <ol className="grid gap-3 text-sm">
                {STEPS.map((step, index) => (
                  <li key={step.title} className="grid grid-cols-[2rem_1fr] gap-3 border-l-2 border-primary bg-muted/40 p-3">
                    <span className="flex h-7 w-7 items-center justify-center bg-primary text-xs font-bold text-primary-foreground">{index + 1}</span>
                    <div><strong className="block">{step.title}</strong><p className="mt-1 text-muted-foreground">{step.body}</p></div>
                  </li>
                ))}
              </ol>
              <div className="border-l-2 border-primary bg-primary/10 p-3 text-sm">
                <strong>Kom ihåg ordningen</strong>
                <p className="mt-1 text-muted-foreground">Nödpausa → publicera och invänta Play → uppdatera text och beteende → aktivera policyn. Annars kan den nya Play-versionen trigga dialogen med föregående versions innehåll.</p>
              </div>
              <div className="border-l-2 border-amber-500 bg-amber-500/10 p-3 text-sm">
                <strong>Viktig säkerhetsregel</strong>
                <p className="mt-1 text-muted-foreground">Ett backendfel eller uteblivet Play-svar får aldrig låsa appen. Obligatoriskt läge används endast när Play Core samtidigt bekräftar en installerbar uppdatering.</p>
              </div>
              <DialogFooter><DialogClose asChild><Button type="button">Stäng</Button></DialogClose></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(19rem,0.65fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> Androidpolicy</CardTitle>
              <CardDescription>Den senaste versionen och tillgängligheten hämtas automatiskt från Google Play.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? <p className="text-sm text-muted-foreground">Laddar policy…</p> : (
                <>
                  <label className="flex items-center justify-between gap-4 border-l-2 border-primary bg-muted/30 p-4">
                    <span><strong className="block text-sm">Aktiv uppdateringskontroll</strong><small className="text-muted-foreground">Pausa här vid incidenter. Play-versionen påverkas inte.</small></span>
                    <Switch checked={settings.enabled} onCheckedChange={(value) => setField("enabled", value)} />
                  </label>

                  <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold">Standardflöde</legend>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {[
                        ["flexible", "Flexible", "Laddas ned i bakgrunden. Användaren kan välja Senare."],
                        ["immediate", "Immediate", "Googles blockerande helskärmsflöde startas direkt."],
                      ].map(([value, label, description]) => (
                        <button
                          key={value}
                          type="button"
                          className={`border-l-2 p-4 text-left ${settings.default_update_type === value ? "border-primary bg-primary/10" : "border-border bg-muted/20"}`}
                          onClick={() => setField("default_update_type", value)}
                        >
                          <strong className="block">{label}</strong>
                          <small className="mt-1 block text-muted-foreground">{description}</small>
                        </button>
                      ))}
                    </div>
                  </fieldset>

                  <div className="space-y-2">
                    <Label htmlFor="minimum-build">Lägsta stödda build</Label>
                    <Input id="minimum-build" type="number" min="1" value={settings.minimum_supported_build} onChange={(event) => setField("minimum_supported_build", Number(event.target.value))} />
                    <p className="text-xs text-muted-foreground">Installerade builds under detta värde använder alltid Immediate. Sätt 1 om ingen befintlig build ska tvångsuppdateras.</p>
                  </div>

                  <div className="grid gap-4">
                    <div className="space-y-2"><Label htmlFor="update-title">Rubrik</Label><Input id="update-title" maxLength={80} value={settings.title} onChange={(event) => setField("title", event.target.value)} /></div>
                    <div className="space-y-2"><Label htmlFor="update-message">Meddelande</Label><Textarea id="update-message" maxLength={500} value={settings.message} onChange={(event) => setField("message", event.target.value)} /></div>
                    <div className="space-y-2"><Label htmlFor="release-notes">Nyheter i versionen</Label><Textarea id="release-notes" className="min-h-32" maxLength={4000} value={settings.release_notes} onChange={(event) => setField("release_notes", event.target.value)} /></div>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" disabled={saving || !settings.enabled} onClick={() => save({ enabled: false })}><PauseCircle className="mr-2 h-4 w-4" /> Nödpausa</Button>
                    <Button type="button" disabled={saving} onClick={() => save()}><Save className="mr-2 h-4 w-4" /> {saving ? "Sparar…" : "Spara policy"}</Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Förhandsvisning</CardTitle><CardDescription>Förutsätter att Play Core rapporterar en tillgänglig uppdatering.</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label htmlFor="simulated-build">Installerad build</Label><Input id="simulated-build" type="number" min="1" value={simulatedBuild} onChange={(event) => setSimulatedBuild(Number(event.target.value))} /></div>
                <div className="border-l-2 border-primary bg-muted/40 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3"><strong>{settings.title}</strong><Badge variant={preview.variant}>{preview.label}</Badge></div>
                  <p className="text-sm text-muted-foreground">{settings.message || preview.description}</p>
                  {settings.release_notes ? <p className="mt-3 whitespace-pre-line border-t pt-3 text-xs text-muted-foreground">{settings.release_notes}</p> : null}
                </div>
                <p className="flex items-start gap-2 text-xs text-muted-foreground">
                  {preview.label === "Pausad" ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" /> : <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />}
                  {preview.description}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Senaste ändringar</CardTitle></CardHeader>
              <CardContent>
                {(settings.history || []).length ? (
                  <div className="space-y-3">
                    {[...(settings.history || [])].reverse().slice(0, 8).map((entry, index) => (
                      <div key={`${entry.changed_at}-${index}`} className="border-l-2 border-primary pl-3 text-sm">
                        <strong>{entry.settings?.default_update_type === "immediate" ? "Immediate" : "Flexible"} · min build {entry.settings?.minimum_supported_build || 1}</strong>
                        <p className="text-xs text-muted-foreground">{formatHistoryDate(entry.changed_at)} · {entry.changed_by || "okänd admin"}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">Ingen historik ännu.</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
